import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faIdCard,
  faInfoCircle,
  faShieldAlt,
  faUserShield,
  faDatabase,
  faFileContract,
  faChevronDown,
  faXmark
} from "@fortawesome/free-solid-svg-icons";
import { login, clearError, resetLoginState } from "../../store/slices/Auth/LoginSlice";
import { DigitalLoginCustomer } from "../../api/authApi";
import {
  openDocumentModal,
  closeDocumentModal,
  fetchDocuments
} from "../../store/slices/Auth/DocumentSlice";
import { resendOtp } from "../../store/slices/Auth/ResendOtpSlice";
import Image from "../../utils/images/mega1.jpg";
import LogoImage from "../../utils/images/logo_21.png";
import MyGov from "../../utils/images/mygov-id.svg";
import VesiqueImage from "../../utils/images/vesiqe.jpg";
import PlaymarketBadge from "../../utils/images/playmarket.png";
import AppStoreBadge from "../../utils/images/appstore.png";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { useNavigate } from 'react-router-dom';
import TechnicalSupport from "../../components/Techniqalsupport";
import PhoneNumberModal from "../../components/PhoneNumberModal/PhoneNumberModal";


export default function AuthLogin() {
  //STATE
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [pinError, setPinError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [pin, setPin] = useState("");
  const [digitalLoginLoading, setDigitalLoginLoading] = useState(false);
  const [digitalLoginError, setDigitalLoginError] = useState("");
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);

  // Technical Support Modal State
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);

  // MyGov Phone Number Modal State
  const [showPhoneModal, setShowPhoneModal] = useState(false);

  const { isDocumentModalOpen, selectedDocument } = useSelector(
    (state: RootState) => state.documents
  );

  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem('selectedLanguage') || 'az';
  });

  const translations = {
    az: {
      ok: "Tamam",
      loginDescription: `<span class=" underline  underline-offset-3">Şəxsi kabinet</span> giriş etmək üçün mobil nömrənizi və FİN kodunuzu daxil edin və ya MyGov ID-dən istifadə edin`,
      mobileNumber: "Mobil nömrə",
      finCode: "Şəxsiyyət vəsiqəsinin FİN-i",
      finCodePlaceholder: "FİN kodu daxil edin",
      finCodeInfo: "FİN kodu haqqında məlumat",
      login: "Daxil olun",
      loading: "Yüklənir...",
      orText: "və ya",
      acceptText: "Davam edərək aşağıdakıları qəbul etmiş sayılacaqsınız:",
      privacyPolicy: "Məxfilik siyasəti",
      userAgreement: "İstifadəçi razılaşması",
      personalDataQuery: "Fərdi məlumatların sorğulanması",
      phoneError: "Nömrə 9 simvoldan ibarət olmalıdır.",
      pinError: "FİN kodu 7 simvoldan ibarət olmalıdır.",
      idCard: "Şəxsiyyət vəsiqəsi",
      privacyPolicyTitle: "Məxfilik Siyasəti",
      userAgreementTitle: "İstifadəçi Razılaşması",
      document: "Sənəd",
      technicalSupport: "Texniki dəstək",
    },
    ru: {
      ok: "Хорошо",
      loginDescription: `<span class="underline  underline-offset-3">Личный кабинет </span> для входа введите свой мобильный номер и ФИН-код или используйте MyGov ID`,
      mobileNumber: "Мобильный номер",
      finCode: "ФИН-код удостоверения личности",
      finCodePlaceholder: "Введите ФИН-код",
      finCodeInfo: "Информация о ФИН-коде",
      login: "Войти",
      loading: "Загрузка...",
      orText: "или",
      acceptText: "Продолжая, вы соглашаетесь со следующим:",
      privacyPolicy: "Политика конфиденциальности",
      userAgreement: "Пользовательское соглашение",
      personalDataQuery: "Запрос персональных данных",
      phoneError: "Номер должен содержать 9 цифр.",
      pinError: "ФИН-код должен содержать 7 символов.",
      idCard: "Удостоверение личности",
      privacyPolicyTitle: "Политика Конфиденциальности",
      userAgreementTitle: "Пользовательское Соглашение",
      document: "Документ",
      technicalSupport: "Техническая поддержка",
    },
    en: {
      ok: "OK",
      loginDescription: `To log in to your <span class=" underline  underline-offset-2">personal cabinet</span>, enter your mobile number and FIN code or use MyGov ID`,
      mobileNumber: "Mobile number",
      finCode: "ID card FIN code",
      finCodePlaceholder: "Enter FIN code",
      finCodeInfo: "Information about FIN code",
      login: "Login",
      loading: "Loading...",
      orText: "or",
      acceptText: "By continuing, you agree to the following:",
      privacyPolicy: "Privacy Policy",
      userAgreement: "User Agreement",
      personalDataQuery: "Personal data query",
      phoneError: "Number must contain 9 digits.",
      pinError: "FIN code must contain 7 characters.",
      idCard: "ID Card",
      privacyPolicyTitle: "Privacy Policy",
      userAgreementTitle: "User Agreement",
      document: "Document",
      technicalSupport: "Technical Support",
    }
  };

  const t = translations[currentLanguage as keyof typeof translations];

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { loading, otpSent, phone, error, message } = useSelector(
    (state: RootState) => state.authLogin
  );

  const languages = [
    { code: 'az', label: 'AZ', name: 'Azərbaycan' },
    { code: 'ru', label: 'RU', name: 'Русский' },
    { code: 'en', label: 'EN', name: 'English' }
  ];

  useEffect(() => {
    dispatch(fetchDocuments());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // MyGov login — phone_number null olduqda popup göstər
  useEffect(() => {
    const needsPhone = localStorage.getItem('needsPhoneNumber') === 'true';
    if (needsPhone) {
      setShowPhoneModal(true);
    }
  }, []);

  const handlePhoneSuccess = async (cleanPhoneNumber: string) => {
    setShowPhoneModal(false);
    // needsPhoneNumber is removed so the modal doesn't pop up again if user navigates back
    localStorage.removeItem('needsPhoneNumber');

    // Nömrə uğurla yazıldı — OTP göndər və OTP səhifəsinə yönləndir
    try {
      await dispatch(
        resendOtp({
          phone_number: cleanPhoneNumber,
        })
      ).unwrap();

      sessionStorage.setItem('otpAllowed', 'true');
      navigate('/otp', {
        state: {
          phoneNumber: cleanPhoneNumber
        }
      });
    } catch (err) {
      console.error("OTP send failed after phone update:", err);
      // Even if it fails (maybe already sent), we should still go to OTP page 
      // so user can use the resend button there.
      sessionStorage.setItem('otpAllowed', 'true');
      navigate('/otp', {
        state: {
          phoneNumber: cleanPhoneNumber
        }
      });
    }
  };

  useEffect(() => {
    if (showModal || isDocumentModalOpen || isSupportModalOpen || showPhoneModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal, isDocumentModalOpen, isSupportModalOpen, showPhoneModal]);

  useEffect(() => {
    if (otpSent && phone) {
      dispatch(resetLoginState());

      navigate('/otp', {
        state: {
          phoneNumber: phone
        }
      });
    }
  }, [otpSent, phone, navigate, dispatch]);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setIsLanguageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    dispatch(clearError());
    setPinError("");
    setPhoneError("");

    const digitsOnly = phoneNumber.replace(/\D/g, '');
    if (!phoneNumber || digitsOnly.length < 12) {
      setPhoneError(t.phoneError);
      return;
    }

    if (!pin || pin.length !== 7) {
      setPinError(t.pinError);
      return;
    }

    const cleanPhoneNumber = phoneNumber.replace(/\s/g, "");
    await dispatch(
      login({
        phoneNumber: cleanPhoneNumber,
        finCode: pin,
      })
    );
  };

  // MyGov Digital Login Handler
  const handleDigitalLogin = async () => {
    setDigitalLoginError("");
    setDigitalLoginLoading(true);

    try {
      const result = await DigitalLoginCustomer(phoneNumber);

      if (result.success && 'login_url' in result.data) {
        window.location.href = result.data.login_url;
      } else {
        const errorMessage = ('error' in result.data && result.data.error)
          ? result.data.error
          : 'Giriş zamanı xəta baş verdi';
        setDigitalLoginError(errorMessage);
      }
    } catch (err) {
      setDigitalLoginError('Gözlənilməz xəta baş verdi');
      console.error('Digital Login Error:', err);
    } finally {
      setDigitalLoginLoading(false);
    }
  };

  const handleDocumentClick = (e: React.MouseEvent, docType: string) => {
    e.preventDefault();
    dispatch(openDocumentModal(docType));
  };

  const getDocumentTitle = (docType: string | undefined) => {
    const titles: Record<string, Record<string, string>> = {
      'PRIVACY_POLICY': {
        az: 'Məxfilik Siyasəti',
        ru: 'Политика Конфиденциальности',
        en: 'Privacy Policy'
      },
      'USER_AGREEMENT': {
        az: 'İstifadəçi Razılaşması',
        ru: 'Пользовательское Соглашение',
        en: 'User Agreement'
      },
    };
    return titles[docType || '']?.[currentLanguage] || t.document;
  };

  const openModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleLanguageChange = (languageCode: string) => {
    localStorage.setItem('selectedLanguage', languageCode);
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Login form */}
      <div className="w-full lg:flex-1 flex  justify-center p-8 relative bg-colormainbrown">
        <div className="w-full max-w-md relative z-10">
          {/* Language Selector - Top Right */}

          <div className="text-center mb-6">
            <div className="flex items-center mb-10">
              <img
                src={LogoImage}
                alt="Meqa Sığorta Logo"
                className="w-28 xs:w-32 sm:w-32 md:w-36 lg:w-40 mr-auto object-contain"
              />
              <div className="flex justify-end mb-1">
                <div className="relative" ref={languageDropdownRef}>
                  <button
                    onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                    className="flex items-center gap-2 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors"
                  >
                    <span className="text-sm font-medium uppercase">{currentLanguage}</span>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={`text-xs transition-transform ${isLanguageDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {/* Language Dropdown */}
                  {isLanguageDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg overflow-hidden z-50">
                      {languages
                        .filter(lang => lang.code !== currentLanguage)
                        .map((language) => (
                          <button
                            key={language.code}
                            onClick={() => handleLanguageChange(language.code)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors flex items-center justify-between group"
                          >
                            <span className="text-gray-700 text-sm font-medium">
                              {language.name}
                            </span>
                            <span className="text-xs text-gray-500 font-semibold uppercase group-hover:text-gray-700">
                              {language.label}
                            </span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <p
            className="text-pink-100 text-md text-left mb-4 font-bold"
            dangerouslySetInnerHTML={{ __html: t.loginDescription }}
          />
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          {/* Digital Login Error Message */}
          {digitalLoginError && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
              <p className="text-red-200 text-sm">{digitalLoginError}</p>
            </div>
          )}

          {/* Success Message */}
          {message && (
            <div className="mb-4 p-4 bg-green-500/20 border border-green-500/50 rounded-lg">
              <p className="text-green-200 text-sm">
                {message} {phone}
              </p>
            </div>
          )}

          {/* Login Form */}
          <div className="space-y-6">
            {/* Phone Number Field */}
            <form>

              <div className="pb-6">
                <label className="block text-sm font-medium text-white mb-2">
                  {t.mobileNumber}

                </label>
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faPhone}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  />

                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneError("");
                      let value = e.target.value;
                      if (!value.startsWith("+994 ")) {
                        setPhoneNumber("+994 ");
                        return;
                      }

                      const digitsOnly = value.slice(5).replace(/\D/g, '');

                      if (digitsOnly.length <= 9) {
                        setPhoneNumber(`+994 ${digitsOnly}`);
                      }
                    }}
                    onFocus={(e) => {
                      if (!phoneNumber) {
                        setPhoneNumber("+994 ");
                        setTimeout(() => {
                          e.target.setSelectionRange(5, 5);
                        }, 0);
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData('text');
                      const digits = pastedText.replace(/\D/g, '');

                      if (digits.length >= 9) {
                        const last9Digits = digits.slice(-9);
                        setPhoneNumber(`+994 ${last9Digits}`);
                      } else if (digits.length > 0) {
                        const currentDigits = phoneNumber.slice(5).replace(/\D/g, '');
                        const newDigits = (currentDigits + digits).slice(0, 9);
                        setPhoneNumber(`+994 ${newDigits}`);
                      }
                    }}
                    placeholder="+994 XX XXX XX XX"
                    className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-transparent"
                    required
                    disabled={loading}
                  />

                </div>
                {phoneError && (
                  <p className="text-red-300 text-sm mt-2">{phoneError}</p>
                )}
              </div>

              {/* Password Field */}
              <div className="pb-6">
                <label className="block text-sm font-medium text-white mb-2">
                  {t.finCode}
                </label>
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faIdCard}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  />
                  <input
                    type="pin"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    maxLength={7}
                    placeholder={t.finCodePlaceholder}
                    className="w-full pl-10 pr-12 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:ring-pink-300   focus:outline-none focus:ring-2 focus:border-transparent"
                    required
                  />
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white opacity-40 cursor-pointer hover:text-pink-200 transition-colors"
                    title={t.finCodeInfo}
                    onClick={openModal}
                  />
                </div>
                {pinError && (
                  <p className="text-red-300 text-sm mt-2">{pinError}</p>
                )}
              </div>


              {/* Sign In Button */}
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={loading}
                className={`w-full text-white font-semibold py-3 px-4 rounded-lg flex items-center justify-center space-x-2 focus:outline-none transition-all ${loading
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:opacity-90 hover:border-pink-800 hover:bg-pink-800'
                  }`}
                style={{ backgroundColor: "#a80067" }}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>{t.loading}</span>


                  </>
                ) : (
                  <span>{t.login}</span>


                )}
              </button>
            </form>
            <div className="flex items-center text-pink-100 text-sm px-5">
              <div className="flex-1 h-px bg-pink-100"></div>
              <span className="px-3">{t.orText}</span>
              <div className="flex-1 h-px bg-pink-100"></div>
            </div>

            {/* MyGov Digital Login Button */}
            <button
              onClick={handleDigitalLogin}
              disabled={digitalLoginLoading}
              className={`w-full bg-white py-3 px-4 rounded-lg flex items-center justify-center shadow-lg transition duration-200 pt-3 ${digitalLoginLoading
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:shadow-xl hover:opacity-90'
                }`}
            >
              {digitalLoginLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 mr-2 text-gray-600" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span className="text-gray-600">Yüklənir...</span>
                </>
              ) : (
                <img
                  src={MyGov}
                  alt="MyGov ID - Digital Login"
                  className="w-28 h-6 pt-1"
                />
              )}
            </button>

            {/* Support Contact */}
            <div className="bg-[#785b6c] p-6 rounded-lg">
              <div className="flex items-start gap-3 text-left text-md text-pink-100">
                <FontAwesomeIcon
                  icon={faShieldAlt}
                  className="text-white mt-0.5 flex-shrink-0"
                />
                <div className="text-sm">
                  {t.acceptText}
                  <ul className="list-none ml-0 mt-2 space-y-2">
                    <li className="flex items-center gap-2">
                      <FontAwesomeIcon
                        icon={faUserShield}
                        className="text-white text-sm w-4 text-center"
                      />
                      <a
                        href="#"
                        onClick={(e) => handleDocumentClick(e, 'PRIVACY_POLICY')}
                        className="hover:underline hover:text-pink-300 text-sm transition-colors underline-offset-2 cursor-pointer"
                      >
                        {t.privacyPolicy}

                      </a>


                    </li>
                    <li className="flex items-center gap-2">
                      <FontAwesomeIcon
                        icon={faFileContract}
                        className="text-white text-sm w-4 text-center"
                      />
                      <a
                        href="#"
                        onClick={(e) => handleDocumentClick(e, 'USER_AGREEMENT')}
                        className="hover:underline hover:text-pink-300 text-sm transition-colors underline-offset-2 cursor-pointer"
                      >
                        {t.userAgreement}

                      </a>
                    </li>
                    <li className="flex items-center gap-2">
                      <FontAwesomeIcon
                        icon={faDatabase}
                        className="text-white text-sm w-4 text-center"
                      />
                      <a className="text-sm transition-colors">
                        {t.personalDataQuery}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6 pt-5 border-t border-white/20">
            <div className="flex justify-center items-center gap-4">
              <a
                href="https://play.google.com/store/apps/details?id=com.megasigorta.app&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={PlaymarketBadge}
                  alt="Google Play-də əldə edin"
                  className="h-12 w-36 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </a>

              <a
                href="https://apps.apple.com/az/app/meqa-s%C4%B1%C4%9Forta/id6751800309"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={AppStoreBadge}
                  alt="App Store-dan yükləyin"
                  className="h-12 w-36 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </a>
            </div>

            {/* Technical Support Link */}
            <div className="mt-4 text-center">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setIsSupportModalOpen(true);
                }}
                className="text-pink-100 hover:text-white text-sm hover:underline transition-colors underline-offset-2 cursor-pointer inline-flex items-center gap-2"
              >
                {t.technicalSupport}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 relative overflow-hidden hidden lg:block">
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            backgroundImage: `url(${Image})`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
            backgroundAttachment: "local",
          }}
        />
      </div>

      {/* FIN Code Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          {/* Modal backdrop - add this wrapper */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={closeModal}
          >
            <div
              className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 text-white text-2xl z-10 bg-colormainbrown hover:bg-[#36232e] rounded-full w-8 h-8 flex items-center justify-center leading-none"
              >
                <FontAwesomeIcon icon={faXmark} className="text-lg" />

              </button>
              {/* Modal content */}
              <div className="p-6">
                <h3 className="text-xl font-semibold p-4 text-gray-800">
                  {t.idCard}

                </h3>
                <img
                  src={VesiqueImage}
                  alt="Şəxsiyyət vəsiqəsi nümunəsi - ID Card Sample"
                  className="w-full h-auto max-h-[50vh] object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Modal */}
      {isDocumentModalOpen && selectedDocument && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 animate-fadeIn"
          onClick={() => dispatch(closeDocumentModal())}
        >
          <div
            className="bg-white rounded-t-2xl w-full sm:w-1/2 h-[80vh] overflow-hidden relative animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => dispatch(closeDocumentModal())}
              className="absolute top-4 right-4 text-gray-600 hover:bg-gray-300 text-2xl z-10 bg-gray-100 rounded-full w-8 h-8 flex items-center justify-center"
            >
              <FontAwesomeIcon icon={faXmark} className="text-lg" />

            </button>

            {/* Modal content */}
            <div className="flex flex-col h-full">
              {/* Fixed header */}
              <div className="px-8 pt-4 pb-4 border-b border-gray-200 bg-white flex-shrink-0">
                <h2 className="text-2xl font-bold text-gray-800 pr-8">
                  {getDocumentTitle(selectedDocument.document_type)}
                </h2>
              </div>
              {/* Scrollable content */}
              <div className="overflow-y-auto px-8 py-6 flex-1">
                <div
                  className="prose max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{ __html: selectedDocument.content }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <TechnicalSupport
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
        currentLanguage={currentLanguage}
      />

      {/* MyGov Phone Number Modal */}
      <PhoneNumberModal
        isOpen={showPhoneModal}
        onSuccess={handlePhoneSuccess}
      />
    </div>

  );
}